import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoHead } from '../hooks/useSeoHead';
import { GuideLanguageSwitch } from '../components/svs-guide/GuideLanguageSwitch';
import '../components/svs-guide/svs-guide.css';

// V2 of 17 planned knowledge-base topics (see the "Content-Seiten pstax.at"
// doc) — structurally identical to V1 (UmsatzsteuerGrundlagenGuide.tsx) and
// the SVS guides. T1 (PublicOnly/InternalOnly audience switch), T2 (page
// registry), T3 (ClickFlow engine) and T4 (a generic GuideArticle template)
// aren't built yet — hand-wired the same way V1 is.
const INDEXABLE = false;

const PATH = '/de/guides/kleinunternehmerregelung/';
const CANONICAL = `https://pstax.at${PATH}`;
export const TITLE_KU_DE = 'Kleinunternehmerregelung in Österreich 2026 | PSTax';
export const DESCRIPTION_KU_DE =
  'Kleinunternehmerregelung 2026: Grenze, Vorsteuerabzug, wann sich der Verzicht lohnt und was auf der Rechnung stehen muss. Vom Steuerberater erklärt.';
const H1 = 'Kleinunternehmerregelung in Österreich 2026: lohnt sich der Verzicht auf Umsatzsteuer?';

const AUTHOR_PERSON = {
  '@type': 'Person',
  name: 'Pascal Schraml',
  jobTitle: 'Steuerberater',
  worksFor: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
};

export const ARTICLE_JSONLD_KU_DE = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: H1,
  datePublished: '2026-10-02',
  dateModified: '2026-10-02',
  inLanguage: 'de',
  author: AUTHOR_PERSON,
  reviewedBy: AUTHOR_PERSON,
  publisher: { '@type': 'Organization', name: 'PSTax Steuerberatung FlexCo', url: 'https://pstax.at' },
  about: 'Kleinunternehmerregelung in Österreich',
};

const TOC_ITEMS: { label: string; id: string }[] = [
  { label: 'Was die Kleinunternehmerregelung eigentlich bedeutet', id: 'bedeutung' },
  { label: 'Der Haken: kein Vorsteuerabzug', id: 'vorsteuerabzug' },
  { label: 'Was zählt überhaupt zur Kleinunternehmergrenze?', id: 'grenze' },
  { label: 'Was passiert, wenn du die Grenze überschreitest?', id: 'ueberschreitung' },
  { label: 'Was auf einer Kleinunternehmer-Rechnung stehen muss', id: 'rechnung' },
  { label: 'Kannst du auf die Kleinunternehmerregelung verzichten?', id: 'verzicht' },
  { label: 'Gilt die Regelung auch in anderen EU-Ländern?', id: 'eu' },
  { label: 'Kleinunternehmerregelung vs. Kleinunternehmerpauschalierung', id: 'pauschalierung' },
  { label: 'Fehler, die wir am häufigsten sehen', id: 'fehler' },
  { label: 'Häufige Fragen', id: 'faq' },
  { label: 'Quellen', id: 'quellen' },
];

// Exported so prerenderRoutes.ts can build the FAQPage JSON-LD from the same
// source instead of duplicating the answers as a second copy of the text.
const plainFaq = (q: string, aPlain: string) => ({ q, aPlain, a: <>{aPlain}</> });

export const FAQS_KU_DE: { q: string; a: React.ReactNode; aPlain: string }[] = [
  plainFaq(
    'Ist die Kleinunternehmergrenze brutto oder netto?',
    'Brutto – 55.000 € inklusive der (nicht verrechneten) Umsatzsteuer.',
    // F-V2
  ),
  plainFaq(
    'Zählen Auslandsumsätze zur Kleinunternehmergrenze?',
    'B2B-Leistungen an ausländische Unternehmen meist nicht, weil ihr Leistungsort im Ausland liegt.',
    // F-V4
  ),
  plainFaq(
    'Was steht auf der Rechnung als Kleinunternehmer?',
    'Keine ausgewiesene Umsatzsteuer, dafür ein Hinweis wie "Umsatzsteuerfrei aufgrund der Kleinunternehmerregelung".',
    // F-V5
  ),
  plainFaq(
    'Brauche ich als Kleinunternehmer eine UID-Nummer?',
    'Nur wenn du mit Unternehmen in anderen EU-Ländern Geschäfte machst – das ändert nichts an deinem steuerfreien Status.',
    // F-V8
  ),
  plainFaq(
    'Wie lange bin ich an den Verzicht gebunden?',
    'Mindestens 5 Kalenderjahre ab dem Jahr, für das du optierst.',
    // F-V6
  ),
  plainFaq(
    'Muss ich als Kleinunternehmer eine Umsatzsteuererklärung abgeben?',
    'In der Regel nein, außer du schuldest ausnahmsweise USt (z. B. durch Reverse Charge) oder hast auf die Regelung verzichtet.',
    // F-V12
  ),
  plainFaq(
    'Was passiert, wenn ich die Grenze um wenig überschreite?',
    'Bis 60.500 € (10 % Toleranz) läuft die Befreiung noch bis Jahresende, darüber endet sie sofort.',
    // F-V3
  ),
];

const ExternalLink: React.FC<{ href: string; children: React.ReactNode; className?: string }> = ({
  href,
  children,
  className = '',
}) => (
  <a href={href} target="_blank" rel="noopener" className={className}>
    {children}
    <span aria-hidden="true"> ↗</span>
    <span className="sr-only">, öffnet in neuem Tab</span>
  </a>
);

const Callout: React.FC<{ tone: 'blue' | 'orange'; label: string; children: React.ReactNode }> = ({
  tone,
  label,
  children,
}) => (
  <div
    className={`my-6 rounded-2xl border-l-4 p-5 ${
      tone === 'blue' ? 'bg-pstax-blue/10 border-pstax-blue' : 'bg-pstax-orange/10 border-pstax-orange'
    }`}
  >
    <p className="text-[11px] font-black uppercase tracking-wider mb-2 text-pstax-navy">{label}</p>
    <p className="text-pstax-navy leading-relaxed">{children}</p>
  </div>
);

const SoonBadge: React.FC = () => (
  <span className="inline-flex items-center text-[10px] font-black uppercase tracking-wider text-pstax-gray bg-pstax-border/60 rounded-full px-2 py-0.5 ml-2 align-middle">
    Bald
  </span>
);

export const KleinunternehmerregelungGuide: React.FC = () => {
  useSeoHead({
    title: TITLE_KU_DE,
    description: DESCRIPTION_KU_DE,
    canonical: CANONICAL,
    indexable: INDEXABLE,
    htmlLang: 'de',
    hreflang: [
      { lang: 'en', href: 'https://pstax.at/en/guides/small-business-vat-exemption/' },
      { lang: 'de', href: 'https://pstax.at/de/guides/kleinunternehmerregelung/' },
      { lang: 'x-default', href: 'https://pstax.at/en/guides/small-business-vat-exemption/' },
    ],
    jsonLd: [
      ARTICLE_JSONLD_KU_DE,
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS_KU_DE.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.aPlain },
        })),
      },
    ],
  });

  return (
    <div className="svs-guide pt-28 pb-24">
      <div className="container mx-auto px-6 max-w-6xl">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="text-sm text-pstax-gray mb-6">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/de/" className="hover:text-pstax-link">
                Startseite
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>Wissensdatenbank</li>
            <li aria-hidden="true">/</li>
            <li className="text-pstax-navy font-semibold" aria-current="page">
              Kleinunternehmerregelung
            </li>
          </ol>
        </nav>

          <article className="min-w-0">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-pstax-blue mb-4">
              Wissensdatenbank · Umsatzsteuer
            </p>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-pstax-navy mb-6 leading-tight">
              {H1}
            </h1>
            <p className="text-xl text-pstax-gray leading-relaxed mb-4">
              Bis 55.000 € Umsatz im Jahr musst du keine Umsatzsteuer verrechnen – klingt nach weniger Aufwand. Der
              Haken: du bekommst dann auch keine Vorsteuer zurück. Ob sich das für dich lohnt, hängt davon ab, wer
              deine Kunden sind und wie viel du selbst einkaufst.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt=""
                  width={36}
                  height={36}
                  decoding="async"
                  className="w-9 h-9 rounded-full object-cover"
                />
              </picture>
              <p className="text-sm text-pstax-gray">
                Stand: <time dateTime="2026-10-01">Oktober 2026</time> · Geprüft von{' '}
                <a href="#autor" className="font-semibold text-pstax-navy hover:text-pstax-link">
                  Pascal Schraml
                </a>
                , Steuerberater, Gründer von PSTax
              </p>
            </div>
            <GuideLanguageSwitch from={PATH} targetLang="EN" label="This page in English →" />

            {/* At a glance */}
            <div className="mt-10 mb-10 bg-white border border-pstax-border rounded-2xl shadow-sm p-6 sm:p-8">
              <h2 className="text-lg font-display font-bold text-pstax-navy mb-4">Auf einen Blick</h2>
              <ul className="space-y-2">
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Kleinunternehmergrenze: 55.000 € brutto/Jahr, 2025 und 2026 unverändert{/* F-V2 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Toleranzgrenze bis 60.500 €: Befreiung läuft bis Jahresende weiter, darüber endet sie sofort{/* F-V3 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Nur in Österreich steuerbare Umsätze zählen zur Grenze – B2B-Leistungen an ausländische
                    Unternehmen meist nicht{/* F-V4 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b border-pstax-border/70 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Als Kleinunternehmer: keine Vorsteuer, dafür auch keine USt auf deinen Rechnungen{/* F-V5 */}
                  </span>
                </li>
                <li className="flex justify-between gap-4 py-1.5 text-sm text-pstax-navy">
                  <span>
                    Freiwilliger Verzicht (Regelbesteuerung) bindet dich mindestens 5 Jahre{/* F-V6 */}
                  </span>
                </li>
              </ul>
            </div>

            <p className="mb-10 leading-relaxed text-pstax-gray">
              Grundlagen zur Umsatzsteuer noch unklar? →{' '}
              <Link
                to="/de/guides/umsatzsteuer-grundlagen/"
                className="text-pstax-link hover:underline font-semibold"
              >
                Umsatzsteuer-Grundlagen (V1)
              </Link>
            </p>

            {/* Table of contents */}
            <nav className="toc-top mb-12" aria-label="Auf dieser Seite">
              <p className="text-[11px] font-black uppercase tracking-wider text-pstax-gray mb-3">
                Auf dieser Seite
              </p>
              <ol className="grid min-[620px]:grid-cols-2 gap-x-8 gap-y-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="flex min-h-[32px] items-center text-sm text-pstax-navy hover:text-pstax-link py-1"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Bedeutung */}
            <h2 id="bedeutung" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Was die Kleinunternehmerregelung eigentlich bedeutet
            </h2>
            <p className="mb-8 leading-relaxed">
              Bleibst du unter 55.000 € Jahresumsatz, verrechnest du keine Umsatzsteuer – auf deinen Rechnungen
              steht ein fixer Hinweis statt eines USt-Betrags{/* F-V2, F-V5 */}. Weniger Verwaltung, aber auch
              weniger Rechte: siehe nächster Punkt.
            </p>

            {/* Vorsteuerabzug */}
            <h2 id="vorsteuerabzug" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Der Haken: kein Vorsteuerabzug
            </h2>
            <p className="mb-8 leading-relaxed">
              Ohne Umsatzsteuer auf deinen Rechnungen gibt es im Gegenzug auch keine Vorsteuer auf deine eigenen
              Einkäufe zurück{/* F-V5 */}. Bei niedrigen Ausgaben fällt das kaum ins Gewicht. Bei größeren
              Anschaffungen – neues Equipment, Software-Abos, eine Einrichtung fürs Büro – kostet dich das reale
              Euro.
            </p>

            {/* Grenze */}
            <h2 id="grenze" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Was zählt überhaupt zur Kleinunternehmergrenze?
            </h2>
            <p className="mb-4 leading-relaxed">
              Nur Umsätze, die in Österreich steuerbar sind, zählen zur Grenze{/* F-V4 */}. B2B-Dienstleistungen
              an Unternehmen im Ausland – EU wie außerhalb – haben ihren Leistungsort meist im Ausland und zählen
              deshalb <strong>nicht</strong> mit.
            </p>
            <Callout tone="orange" label="Klingt einfach, aber">
              Das heißt, du kannst hohe Umsätze mit ausländischen Kunden machen und trotzdem in Österreich
              Kleinunternehmer bleiben – brauchst dafür aber oft trotzdem eine UID-Nummer und musst eine
              Zusammenfassende Meldung abgeben{/* F-V8 */}.
            </Callout>

            {/* Überschreitung */}
            <h2 id="ueberschreitung" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Was passiert, wenn du die Grenze überschreitest?
            </h2>
            <p className="mb-4 leading-relaxed">
              Bis 60.500 € (10 % Toleranz) läuft die Befreiung noch bis Jahresende weiter. Darüber endet sie{' '}
              <strong>sofort</strong> – ab genau der Rechnung, die die Grenze reißt{/* F-V3 */}. Ab diesem
              Moment musst du Umsatzsteuer ausweisen.
            </p>
            <Callout tone="orange" label="Klingt einfach, aber">
              Wer das zu spät bemerkt, schuldet USt, die er nie verrechnet hat – nachträglich beim Kunden
              draufzurechnen ist in der Praxis oft nicht möglich.
            </Callout>

            {/* Rechnung */}
            <h2 id="rechnung" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Was auf einer Kleinunternehmer-Rechnung stehen muss
            </h2>
            <p className="mb-8 leading-relaxed">
              Keine ausgewiesene Umsatzsteuer, dafür ein Hinweis wie "Umsatzsteuerfrei aufgrund der
              Kleinunternehmerregelung"{/* F-V5 */}. Steht trotzdem USt auf der Rechnung, schuldest du sie dem
              Finanzamt – unabhängig davon, ob du eigentlich befreit wärst.
            </p>

            {/* Verzicht */}
            <h2 id="verzicht" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Kannst du auf die Kleinunternehmerregelung verzichten?
            </h2>
            <p className="mb-6 leading-relaxed">
              Ja, mit einem Regelbesteuerungsantrag (Formular U12). Das kann sich lohnen, wenn deine Kunden
              größtenteils Unternehmen sind, die die Vorsteuer ohnehin zurückbekommen, oder wenn du höhere
              Investitionen planst. Klingt einfach, aber: der Verzicht bindet dich mindestens{' '}
              <strong>5 Kalenderjahre</strong>{/* F-V6 */} – eine Entscheidung, die man nicht aus dem Bauch
              heraus trifft.
            </p>

            <div className="overflow-x-auto mb-10">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-pstax-navy">
                    <th scope="col" className="text-left py-2 pr-4 font-bold text-pstax-navy">
                      Kleinunternehmer (keine USt) passt, wenn …
                    </th>
                    <th scope="col" className="text-left py-2 font-bold text-pstax-navy">
                      Regelbesteuerung (mit USt) passt, wenn …
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-pstax-border align-top">
                    <td className="py-2 pr-4">
                      deine Kunden größtenteils Privatpersonen sind (die bekommen keine Vorsteuer zurück)
                    </td>
                    <td className="py-2">deine Kunden größtenteils Unternehmen sind (für die kostet USt nichts)</td>
                  </tr>
                  <tr className="border-b border-pstax-border align-top">
                    <td className="py-2 pr-4">du wenig investierst oder einkaufst</td>
                    <td className="py-2">du größere Anschaffungen planst (Equipment, Auto, Software)</td>
                  </tr>
                  <tr className="align-top">
                    <td className="py-2 pr-4">du möglichst wenig Verwaltung willst</td>
                    <td className="py-2">
                      du viel mit ausländischen B2B-Kunden arbeitest und Vorsteuer zurückholen willst
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* EU */}
            <h2 id="eu" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Gilt die Regelung auch in anderen EU-Ländern?
            </h2>
            <p className="mb-8 leading-relaxed">
              Seit 2025 kannst du die österreichische Kleinunternehmerbefreiung unter bestimmten Voraussetzungen
              auch für Umsätze in anderen EU-Staaten nutzen, solange dein EU-weiter Jahresumsatz 100.000 € nicht
              übersteigt – dafür ist eine eigene Registrierung in Österreich nötig{/* F-V13 */}.
            </p>

            {/* Pauschalierung */}
            <h2 id="pauschalierung" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Kleinunternehmerregelung ist nicht dasselbe wie Kleinunternehmerpauschalierung
            </h2>
            <p className="mb-8 leading-relaxed">
              Häufige Verwechslung: Die Kleinunternehmerregelung betrifft die Umsatzsteuer (ob du USt
              verrechnest). Die Kleinunternehmerpauschalierung ist eine ganz andere, eigene Regel bei der
              Einkommensteuer (wie dein Gewinn pauschal berechnet wird) – mehr dazu im Guide zur Gewinnermittlung
              <SoonBadge />. Die eine Regel sagt nichts über die andere.
            </p>

            {/* Fehler */}
            <h2 id="fehler" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Fehler, die wir am häufigsten sehen
            </h2>
            <ul className="mb-4 space-y-3 list-disc pl-6 leading-relaxed">
              <li>
                EU-B2B-Umsätze fälschlich zur 55.000-€-Grenze dazugerechnet – und dadurch unnötig auf USt
                umgestiegen
              </li>
              <li>Trotz Kleinunternehmer-Status versehentlich eine Rechnung mit USt ausgestellt</li>
              <li>Grenze überschritten, aber Monate weiter ohne USt fakturiert</li>
              <li>Kleinunternehmerregelung (USt) mit Kleinunternehmerpauschalierung (ESt) verwechselt</li>
              <li>Freiwillig auf die Regelung verzichtet, ohne die 5-Jahres-Bindung einzupreisen</li>
            </ul>

            <p className="mb-10 leading-relaxed">
              <strong>Was heißt das für dich?</strong> Ob Kleinunternehmer-Status oder Verzicht zu dir passt,
              hängt von deinen Kunden und Ausgaben ab – das rechnen wir dir im kostenlosen Erstgespräch in 30
              Minuten durch.{' '}
              {/* TODO: PublicOnly/InternalOnly once T1 ships.
                  InternalOnly: "Fragen dazu? Schreib uns im Portal oder an clients@pstax.at" */}
              <Link to="/de/#fit-check" className="text-pstax-link hover:underline font-semibold">
                Kostenloses Erstgespräch buchen
              </Link>
              .
            </p>

            {/* FAQ */}
            <h2 id="faq" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Häufige Fragen
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS_KU_DE.map((faq) => (
                <details key={faq.q} className="group bg-white border border-pstax-border rounded-2xl overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 min-h-[44px] flex items-center justify-between gap-4 font-semibold text-pstax-navy">
                    <span>{faq.q}</span>
                    <span aria-hidden="true" className="shrink-0 text-pstax-blue transition-transform group-open:rotate-180">
                      ▾
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-pstax-gray leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>

            <p className="mb-10 leading-relaxed text-sm text-pstax-gray">
              <strong className="text-pstax-navy">Weiterlesen</strong> 2-Minuten-Kurzinfo zu diesem Thema
              <SoonBadge /> ·{' '}
              <Link to="/de/guides/umsatzsteuer-grundlagen/" className="text-pstax-link hover:underline">
                Umsatzsteuer-Grundlagen (V1)
              </Link>{' '}
              · UID-Nummer
              <SoonBadge /> · Gewinnermittlung: Pauschalierung oder Einnahmen-Ausgaben-Rechnung
              <SoonBadge />
            </p>

            {/* Quellen */}
            <h2 id="quellen" className="text-3xl font-display font-bold text-pstax-navy mt-12 mb-4">
              Quellen
            </h2>
            <ul className="mb-4 space-y-4 leading-relaxed">
              <li>
                usp.gv.at / wko.at:{' '}
                <ExternalLink
                  href="https://www.usp.gv.at/themen/steuern-finanzen/umsatzsteuer-ueberblick/weitere-informationen-zur-umsatzsteuer/weitere-steuertatbestaende-und-befreiungen/kleinunternehmen.html"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Kleinunternehmerregelung
                </ExternalLink>
                {/* F-V2, F-V3, F-V4, F-V5, F-V6 */}
              </li>
              <li>
                wko.at:{' '}
                <ExternalLink
                  href="https://www.wko.at/steuern/kleinunternehmerregelung-oesterreich-faq"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  Kleinunternehmerregelung Österreich FAQ
                </ExternalLink>
                {/* F-V5 */}
              </li>
              <li>
                wko.at:{' '}
                <ExternalLink
                  href="https://www.wko.at/steuern/unternehmensgruenderinnen-uid-nummern-faq"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  UID-Nummern FAQ
                </ExternalLink>
                {/* F-V8 */}
              </li>
              <li>
                RIS:{' '}
                <ExternalLink
                  href="https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10004873&Paragraf=6"
                  className="font-semibold text-pstax-link hover:underline"
                >
                  § 6 UStG
                </ExternalLink>
                {/* F-V2, F-V13 */}
              </li>
            </ul>

            {/* CTA */}
            <div className="mt-14 bg-pstax-navy text-white rounded-3xl p-8 sm:p-10">
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-3">
                Unsicher, ob sich der Verzicht für dich lohnt?
              </h2>
              <p className="text-white/75 leading-relaxed mb-6 max-w-2xl">
                Ob Kleinunternehmer-Status oder Regelbesteuerung zu dir passt, hängt von deinen Kunden und
                Ausgaben ab – und von einer 5-Jahres-Bindung, wenn du dich umentscheidest. Im kostenlosen
                30-Minuten-Erstgespräch rechnen wir das für dich durch.{' '}
                {/* TODO: PublicOnly/InternalOnly once T1 ships.
                    InternalOnly: "Schon Klient:in? Fragen dazu beantworten wir dir im Portal oder unter
                    clients@pstax.at." */}
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Link to="/de/#fit-check" className="btn-accent">
                  Gratis Erstgespräch buchen
                </Link>
              </div>
              <p className="text-xs text-white/50">
                PSTax betreut über 600 Klient:innen in Österreich, Fixpreise ab 800 €/Jahr, auf Deutsch und
                Englisch.
              </p>
            </div>

            {/* Autorenbox */}
            <div id="autor" className="mt-10 bg-white border border-pstax-border rounded-2xl p-6 sm:p-8 flex gap-5 items-start">
              <picture className="shrink-0">
                <source srcSet="/pascal-schraml.webp" type="image/webp" />
                <img
                  src="/pascal-schraml.jpg"
                  alt="Pascal Schraml, österreichischer Steuerberater und Gründer von PSTax."
                  width={76}
                  height={76}
                  loading="lazy"
                  decoding="async"
                  className="w-[76px] h-[76px] rounded-full object-cover"
                />
              </picture>
              <div>
              <p className="font-display font-bold text-pstax-navy text-lg mb-0.5">Pascal Schraml</p>
              <p className="text-sm text-pstax-gray mb-3">Steuerberater, Gründer von PSTax</p>
              <p className="text-sm text-pstax-gray leading-relaxed">
                Berät seit 2023 Selbstständige und Freelancer in Österreich, Betreuung auf Deutsch und Englisch,
                über 600 Klient:innen.
              </p>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-6 bg-pstax-bg rounded-2xl border border-pstax-border">
              <p className="text-xs text-pstax-gray leading-relaxed">
                Allgemeine Information, ersetzt keine individuelle Steuerberatung. Alle Zahlen gelten für 2026 und
                ändern sich jährlich. PSTax (Pascal Schraml Steuerberatung FlexCo) ist Mitglied der Kammer der
                Steuerberater:innen und Wirtschaftsprüfer:innen (KSW).
              </p>
            </div>
          </article>
      </div>
    </div>
  );
};
